import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AiChatProvider,
  deserializeMessages,
  serializeForStorage,
  trimHistory,
  useAiChatContext,
  type AiBackendAdapter,
  type AiMessage,
  type AiSerializedMessage
} from 'juneau';
import { debounce } from 'es-toolkit';
import { AI_CHAT_MODEL_HISTORY_LIMIT, AI_CHAT_STORED_HISTORY_LIMIT } from '@insio/shared-types';
import {
  cancelProposal,
  confirmProposal,
  fetchChatHistory,
  HISTORY_CHANNEL,
  saveChatHistory
} from './chat-history.api';
import { AI_SESSION_ID } from './session';
import { takeProposalSecrets } from './proposal-secrets';
import { fitHistoryToByteBudget } from './history-byte-budget';
import { isEmptyAssistantMessage } from './empty-assistant-message';
import { useApolloClient } from '@apollo/client/react';
import { ProcurementOverviewDocument } from '@/graphql';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';
import { useToastHelpers } from '@/hooks/use-toast-helpers';

/** Max messages sent to the LLM per request — caps token cost. Persistence keeps more. */
const HISTORY_LIMIT = AI_CHAT_MODEL_HISTORY_LIMIT;

/** Max messages persisted and restored after reload — the backend's limit. */
const PERSISTED_HISTORY_LIMIT = AI_CHAT_STORED_HISTORY_LIMIT;

/** Debounce window for history persistence — coalesces rapid message updates. */
const PERSIST_DEBOUNCE_MS = 800;

interface HistoryPersister {
  /** Enables persistence — call only after the initial load settled definitively. */
  enable: () => void;
  onMessagesChange: (messages: AiMessage[]) => void;
  /** True once the user sent a message in THIS tab — such a tab owns its conversation. */
  isDirty: () => boolean;
  /** Sends any pending save immediately — call on unmount. */
  flush: () => void;
}

/** Debounced, single-in-flight history persister. Disabled until the initial load settles. */
function createHistoryPersister(notifySaved: () => void): HistoryPersister {
  let enabled = false;
  let inFlight = false;
  let dirty = false;
  let pending: AiSerializedMessage[] | null = null;

  const send = async (payload: AiSerializedMessage[]): Promise<void> => {
    inFlight = true;
    try {
      await saveChatHistory(payload);
      notifySaved();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[ai-chat] Failed to persist chat history', err);
    } finally {
      inFlight = false;
      if (pending) {
        const next = pending;
        pending = null;
        void send(next);
      }
    }
  };

  const debouncedSend = debounce((messages: AiSerializedMessage[]) => {
    const payload = fitHistoryToByteBudget(messages);
    // Nothing fits — keep the saved history rather than clear it with an empty save.
    if (payload.length === 0) return;
    // Only one save in flight — queue the latest payload and send it afterwards.
    if (inFlight) {
      pending = payload;
      return;
    }
    void send(payload);
  }, PERSIST_DEBOUNCE_MS);

  return {
    enable: () => {
      enabled = true;
    },
    onMessagesChange: (changed: AiMessage[]) => {
      if (!enabled) return;
      // A greeting the server declined leaves an empty placeholder — never worth saving.
      const messages = changed.filter(message => !isEmptyAssistantMessage(message));
      const firstUserIdx = messages.findIndex(m => m.role === 'user');
      // Persist only conversations the user participated in — skips assistant-only
      // greetings and prevents re-saving right after a reset cleared the history.
      if (firstUserIdx === -1) {
        // Reset/cleared state — drop any pending pre-reset save so it can't
        // fire later and overwrite the cleared server history.
        debouncedSend.cancel();
        pending = null;
        dirty = false;
        return;
      }
      dirty = true;
      const withoutGreeting = messages.slice(firstUserIdx);
      debouncedSend(serializeForStorage(trimHistory(withoutGreeting, PERSISTED_HISTORY_LIMIT)));
    },
    isDirty: () => dirty,
    flush: () => {
      debouncedSend.flush();
    }
  };
}

/** Fetches history once on mount and resets the chat into it. */
function HistoryLoader({
  persister,
  channel
}: {
  persister: HistoryPersister;
  channel: BroadcastChannel | null;
}) {
  const { reset, messages } = useAiChatContext();
  const messagesRef = useRef(messages);
  messagesRef.current = messages;

  useEffect(() => {
    let cancelled = false;
    fetchChatHistory()
      .then(raw => {
        if (cancelled) return;
        const loaded = deserializeMessages(raw);
        // Don't clobber a conversation the user already started while loading.
        if (loaded.length > 0 && messagesRef.current.length === 0) reset(loaded);
        // Load settled definitively (success or confirmed-empty) — safe to persist now.
        persister.enable();
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        // Persistence stays disabled — a transient failure must not overwrite server history.
        // eslint-disable-next-line no-console
        console.error('[ai-chat] Failed to load chat history', err);
      });
    return () => {
      cancelled = true;
    };
  }, [persister, reset]);

  // History is one row per user (last-write-wins) — when another tab saves,
  // an idle tab adopts that state instead of overwriting it with stale messages later.
  useEffect(() => {
    if (!channel) return;
    const onMessage = () => {
      // A tab with its own active conversation keeps it — the user owns both states.
      if (persister.isDirty()) return;
      fetchChatHistory()
        .then(raw => {
          if (persister.isDirty()) return;
          const loaded = deserializeMessages(raw);
          if (loaded.length > 0) reset(loaded);
        })
        .catch(() => {
          // Best-effort sync — the next explicit load will recover.
        });
    };
    channel.addEventListener('message', onMessage);
    return () => channel.removeEventListener('message', onMessage);
  }, [channel, persister, reset]);

  return null;
}

/**
 * Drop-in for AiChatProvider — mounts immediately (no null flash),
 * loads Postgres history after mount, persists on every settled turn.
 */
export function AiChatWithHistory({
  adapter,
  children
}: {
  adapter: AiBackendAdapter;
  children: ReactNode;
}) {
  // Channel lifecycle is effect-owned: StrictMode's mount→cleanup→remount cycle closes
  // the previous instance, so a memoized channel would stay closed after the remount.
  const channelRef = useRef<BroadcastChannel | null>(null);
  const [channel, setChannel] = useState<BroadcastChannel | null>(null);

  const persister = useMemo(
    () => createHistoryPersister(() => channelRef.current?.postMessage('saved')),
    []
  );

  useEffect(() => {
    // BroadcastChannel is broadly supported; guard for non-browser test environments.
    const instance =
      typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(HISTORY_CHANNEL);
    channelRef.current = instance;
    setChannel(instance);
    return () => {
      persister.flush();
      // Null the ref first — a save settling after unmount must not post to a closed channel.
      channelRef.current = null;
      setChannel(null);
      instance?.close();
    };
  }, [persister]);

  const t = useFeatureTranslation('aiChat');
  const { showSuccess, showError } = useToastHelpers();

  /**
   * The outcome is reported by toast only, never by the assistant. The chat transport has
   * no channel for it: a `sendGreeting` hint is dropped by the adapter and flipped into
   * GREETING mode, which the server's once-per-day gate then answers with an empty turn.
   *
   * Handler messages stay out of the UI too — they are English server strings, and an
   * English sentence in a Czech UI is worse than a localized generic one.
   */
  // A double-click would otherwise fire the mutation twice for one card.
  const confirmedProposals = useRef(new Set<string>());
  const apolloClient = useApolloClient();

  const handleProposalConfirm = useCallback(
    (proposalId: string) => {
      // Taken first, so a repeated click never leaves secrets behind in memory.
      const secrets = takeProposalSecrets(proposalId);
      if (confirmedProposals.current.has(proposalId)) return;
      confirmedProposals.current.add(proposalId);

      // Secrets typed into the card's form travel only in this request, never in the messages.
      void confirmProposal(proposalId, secrets)
        .then(result => {
          // The card flips to "confirmed" on its own; the toast states whether the
          // action behind it actually ran.
          if (result.success) {
            showSuccess(t('proposals.toast.confirmed'));
            // A decision made here changes what waits on the user; only queries on screen reload.
            // A failed reload only leaves the panel stale until its next load.
            apolloClient.refetchQueries({ include: [ProcurementOverviewDocument] }).catch(() => {});
          } else showError(t('proposals.toast.failed'));
        })
        // Network errors, ForbiddenException on ownership mismatch and 429s all reject —
        // without this the card shows success for an action that never ran.
        .catch(() => {
          // The mutation never returned an answer, so the proposal may still be unconsumed.
          // Releasing the guard lets the user retry instead of facing a dead card.
          confirmedProposals.current.delete(proposalId);
          showError(t('proposals.toast.failed'));
        });
    },
    [apolloClient, showError, showSuccess, t]
  );

  const handleProposalCancel = useCallback(
    (proposalId: string) => {
      takeProposalSecrets(proposalId);
      void cancelProposal(proposalId).catch(() => {
        showError(t('proposals.toast.cancelFailed'));
      });
    },
    [showError, t]
  );

  return (
    <AiChatProvider
      adapter={adapter}
      context={{ sessionId: AI_SESSION_ID }}
      historyLimit={HISTORY_LIMIT}
      onMessagesChange={persister.onMessagesChange}
      onProposalConfirm={handleProposalConfirm}
      onProposalCancel={handleProposalCancel}
    >
      <HistoryLoader persister={persister} channel={channel} />
      {children}
    </AiChatProvider>
  );
}
